import { columnToIndex, indexToColumn, splitRange, isCell, isRange } from "../../../utils/util";
import { functionNames, precedence, operatorList, singleOperands } from "./FormulaConstants";
const functions = {
  utilities: {
    getValues: (parameters) => {
      return parameters.map((param) => {
        if (param.type === "range")
          return functions
            .getRangeValue(param.value)
            .flat()
            .map((x) => x.value);
        else if (param.type === "cell") return functions.getCellValue(param.value).value;
        else return param.value;
      });
    },
    getTitles: (parameters) => {
      return parameters.map((param) => {
        if (param.type === "cell") return functions.getCellTitle(param.value);
        if (param.type === "range") return functions.getRangeTitle(param.value);
        return param.value;
      });
    },
    customFilter: (parameters) => {
      const dataRange = parameters[0];

      return dataRange.filter((item, index) => {
        for (let j = 1; j < parameters.length; j += 2) {
          if (!functions.utilities.evaluateCriteria(parameters[j][index], parameters[j + 1])) {
            return false;
          }
        }
        return true;
      });
    },
    evaluateCriteria: (value, criteria) => {
      // Check for wildcards
      if (String(criteria).includes("*") || String(criteria).includes("?")) {
        const pattern = new RegExp("^" + criteria.split("*").map(escapeRegExp).join(".*").replace(/\?/g, ".") + "$");
        return pattern.test(value);
      }

      // Check for comparison operators
      const operators = /(<>)|([<>]=?|=)/.exec(criteria);
      if (operators) {
        const operator = operators[0];
        const numericValue = parseFloat(value);
        const numericCriteria = parseFloat(criteria.substr(operator.length));
        switch (operator) {
          case ">":
            return numericValue > numericCriteria;
          case ">=":
            return numericValue >= numericCriteria;
          case "<":
            return numericValue < numericCriteria;
          case "<=":
            return numericValue <= numericCriteria;
          case "=":
            return numericValue == numericCriteria;
          case "<>":
            return numericValue != numericCriteria;
          default:
            return false;
        }
      }

      // Default case: strict equality
      return value == criteria;
    },
    getBooleanValue: (value) => {
      if (!isNaN(value)) return Number(value);
      else if (["true", "false"].includes(value)) return Number(value === "true");
      else return Number(Boolean(value));
    },
  },
  abs: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const result = Math.abs(parameters);
    return { value: result };
  },
  sum: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat(2);
    const result = parameters.reduce((total, num) => total + Number(num), 0);
    return { value: result };
  },
  max: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat(2);
    const result = Math.max(...parameters);
    return { value: result };
  },
  min: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat(2);
    const result = Math.min(...parameters);
    return { value: result };
  },
  average: (parameters) => {
    const data = functions.utilities.getValues(parameters).flat(2);
    const result = functions["sum"](parameters).value / data.length;
    return { value: result };
  },
  if: (parameters) => {
    const values = functions.utilities.getValues(parameters).flat();
    return values[0] ? parameters[1] : parameters[2];
  },
  ifs: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    for (let i = 0; i < parameters.length; i += 2) {
      const condition = parameters[i];
      const value = parameters[i + 1];
      if (condition) return { value };
    }
  },
  and: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    const result = parameters.every((param) => !!param) + "";
    return { value: result };
  },
  or: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    const result = parameters.some((param) => !!param) + "";
    return { value: result };
  },
  not: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    const result = !parameters[0] + "";
    return { value: result };
  },
  choose: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    const result = parameters[parameters[0]];
    return { value: result };
  },
  sumifs: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const filteredData = functions.utilities["customFilter"](parameters);
    const result = functions["sum"](filteredData.map((item) => ({ value: item })));
    return { value: result.value };
  },
  averageifs: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const filteredData = functions.utilities["customFilter"](parameters).map((item) => ({ value: item }));
    const result = functions["sum"](filteredData).value / filteredData.length;
    return { value: result };
  },
  formulatext: (parameters) => {
    parameters = functions.utilities.getTitles(parameters).flat();
    const result = parameters[0];
    return { value: result };
  },
  count: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat(2);
    const result = parameters.reduce((total, num) => total + (isNaN(num) ? 0 : 1), 0);
    return { value: result };
  },
  counta: (parameters) => {
    parameters = functions.utilities.getTitles(parameters).flat(2);
    const result = parameters.reduce((total, num) => total + (num === "" ? 0 : 1), 0);
    return { value: result };
  },
  countifs: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    parameters.unshift(parameters[0]);
    const filteredData = functions.utilities["customFilter"](parameters);
    const result = filteredData.length;
    return { value: result };
  },
  column: (parameters, cellLabel) => {
    const cell = parameters[0]?.value ?? cellLabel;
    const result = functions.getColumnNumber(cell);
    return { value: result };
  },
  columns: (parameters) => {
    const range = parameters[0].value;
    const data = range.split(":");
    const result =
      functions.column([{ value: data[1] ?? data[0], type: "cell" }]).value -
      functions.column([{ value: data[0], type: "cell" }]).value +
      1;
    return { value: result };
  },
  row: (parameters, cellLabel) => {
    const cell = parameters[0]?.value ?? cellLabel;

    const result = functions.getRowNumber(cell);
    return { value: result };
  },
  rows: (parameters) => {
    const [startCell, endCell] = parameters[0].value.split(":");
    const result =
      functions.row([{ value: endCell, type: "cell" }]).value -
      functions.row([{ value: startCell, type: "cell" }]).value +
      1;
    return { value: result };
  },
  left: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const [text, numChars] = parameters;
    const result = parameters[0].substring(text, numChars ?? 1);
    return { value: result };
  },
  right: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const [text, numChars] = parameters;
    const data = numChars ? text.length - numChars ?? 1 : text.length - 1;
    const result = parameters[0].substring(data);
    return { value: result };
  },
  mid: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const [text, startNum, numChars] = parameters;
    const result = text.substring(startNum - 1, startNum + numChars - 1);
    return { value: result };
  },
  len: (parameters) => {
    const text = functions.utilities.getValues(parameters)[0].toString();
    const result = text.length;
    return { value: result };
  },
  replace: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const [oldText, startNum, numChars, newText] = parameters;
    const endIdx = startNum + numChars - 1;

    const partBefore = oldText.substring(0, startNum - 1);
    const partAfter = oldText.substring(endIdx);

    const result = partBefore + newText + partAfter;
    return { value: result };
  },
  round: (parameters) => {
    parameters = functions.utilities.getValues(parameters);
    const [number, numDigits] = parameters;
    if (numDigits >= 0) {
      const multiplier = Math.pow(10, numDigits);
      const result = Math.round(number * multiplier) / multiplier;
      return { value: result };
    } else {
      const multiplier = Math.pow(10, -numDigits);
      const result = Math.round(number / multiplier) * multiplier;
      return { value: result };
    }
  },
  index: (parameters) => {
    let [range] = parameters;
    let [, rowNum, columnNum] = functions.utilities.getValues(parameters);
    const [startColumn, startRow, endColumn, endRow] = splitRange(range.value);
    if (isNaN(rowNum) || isNaN(columnNum)) {
      if (startColumn === endColumn) {
        columnNum = 1;
      } else if (startRow === endRow) {
        columnNum = rowNum;
        rowNum = 1;
      }
    }
    const column = indexToColumn(columnToIndex(startColumn) + +columnNum - 1);
    const row = +startRow + +rowNum - 1;
    return { value: column + row, type: "cell" };
  },
  match: (parameters) => {
    let [lookupValue, lookupArray, matchType] = functions.utilities.getValues(parameters);
    lookupArray = lookupArray.flat();
    switch (matchType) {
      case 0: {
        lookupArray = lookupArray.map((x) => String(x).toLowerCase());
        const index = lookupArray.findIndex((item) =>
          functions.utilities.evaluateCriteria(item, String(lookupValue).toLowerCase())
        );
        return { value: index + 1 };
      }
      case -1: {
        let minVal = Infinity;
        let index = -1;
        for (let i = 0; i < lookupArray.length; i++) {
          if (lookupArray[i] >= lookupValue && lookupArray[i] < minVal) {
            minVal = lookupArray[i];
            index = i;
          }
        }
        return { value: index + 1 };
      }
      default: {
        let maxVal = -Infinity;
        let index = -1;
        for (let i = 0; i < lookupArray.length; i++) {
          if (lookupArray[i] <= lookupValue && lookupArray[i] > maxVal) {
            maxVal = lookupArray[i];
            index = i;
          }
        }
        return { value: index + 1 };
      }
    }
  },
  getifs: (parameters) => {
    const functionEnums = {
      1: "count",
      2: "counta",
      3: "sum",
      4: "average",
      5: "max",
      6: "min",
      7: "first",
      8: "last",
      9: "most",
      10: "least",
    };
    const behaviorFunctions = {
      0: () => true,
      1: (item) => item != 0,
      2: (item) => !isNaN(item),
      3: (item) => isNaN(item),
    };
    const [dataRange, functionNumber, behavior = 0, ...criterias] = functions.utilities.getValues(parameters);
    const filteredData = functions.utilities["customFilter"]([dataRange, ...criterias]).filter((item) =>
      behaviorFunctions[behavior](item)
    );
    return functions[functionEnums[functionNumber]](filteredData.map((item) => ({ value: item })));
  },
  first: (parameters) => {
    const result = parameters[0].value;
    return { value: result };
  },
  last: (parameters) => {
    const result = parameters[parameters.length - 1].value;
    return { value: result };
  },
  least: (parameters) => {
    const frequency = {};
    parameters.forEach((element) => {
      frequency[element.value] = (frequency[element.value] || 0) + 1;
    });

    const result = Object.keys(frequency).reduce((a, b) => (frequency[a] <= frequency[b] ? a : b));
    return { value: result };
  },
  most: (parameters) => {
    const frequency = {};
    parameters.forEach((element) => {
      frequency[element.value] = (frequency[element.value] || 0) + 1;
    });
    const result = Object.keys(frequency).reduce((a, b) => (frequency[a] >= frequency[b] ? a : b));
    return { value: result };
  },
  hyperlink: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat();
    return { value: parameters[1], link: parameters[0] };
  },
  date: (parameters) => {
    parameters = functions.utilities.getValues(parameters).flat(2);
    const date = new Date(parameters[0], parameters[1] - 1, parameters[2]);
    const MS_PER_DAY = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
    const START_DATE = new Date(1900, 0, 1); // January 1, 1900

    // Calculate the number of days since January 1, 1900
    const daysSinceStart = Math.floor((date.getTime() - START_DATE.getTime()) / MS_PER_DAY) + 2;
    return { value: daysSinceStart };
  },
};

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
export function isOperator(token) {
  return operatorList.includes(token);
}
function isFunction(token) {
  return !isStringLiteral(token) && functionNames.includes(token?.toLowerCase());
}
function isStringLiteral(token) {
  return new RegExp(/^".*"$/).test(token);
}

export function parseStringToType(input) {
  if (input?.toLowerCase() === "true" || input?.toLowerCase() === "false")
    return { value: input?.toLowerCase() === "true" };
  else if (input !== "" && !isNaN(input) && typeof input !== "boolean") return { value: parseFloat(input) };
  else return { value: input };
}
function separateOperatorsAndOperands(expression) {
  // Use regular expression to tokenize the expression
  let tokens = expression.match(/(\d+(\.\d+)?)|([<>]=?|=)|[%&|*^,+:\-\\/()]|("[^"]*")|[a-zA-Z0-9]*/gu);
  // Filter out whitespace tokens
  const result = [];
  tokens = tokens.filter((token) => token.trim() !== "");

  tokens
    .map((token, index) => {
      if (token === "(" && tokens[index + 1] === ",") return ["(", 0];
      else if (token === "," && (tokens[index + 1] === "," || tokens[index + 1] === ")")) return [",", 0];
      else if (token === "(" && tokens[index + 1] === ")") return ["(", null];
      return token;
    })
    .flat()
    .forEach((token) => {
      if (["-", "+"].includes(token)) {
        const lastElement = result[result.length - 1];
        if (!isCell(lastElement) && isNaN(lastElement) && lastElement !== ")") {
          result.push(Number(`${token}1`));
          result.push("*");
        } else result.push(token);
      } else result.push(token);
    });
  return result;
}
export function infixToPostfix(infixExpression) {
  const output = [];
  const operatorStack = [];
  const tokens = separateOperatorsAndOperands(infixExpression);
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (
      token === null ||
      !isNaN(parseFloat(token)) ||
      isCell(token) ||
      isRange(token) ||
      isStringLiteral(token) ||
      token?.toLowerCase() === "true" ||
      token?.toLowerCase() === "false"
    ) {
      output.push(token);
    } else if (isFunction(token)) {
      operatorStack.push(token);
      i++;
    } else if (token === "(") {
      operatorStack.push(token);
    } else if (token === ")") {
      while (
        operatorStack.length &&
        !isFunction(operatorStack[operatorStack.length - 1]) &&
        operatorStack[operatorStack.length - 1] !== "("
      ) {
        output.push(operatorStack.pop());
      }
      if (isFunction(operatorStack[operatorStack.length - 1])) output.push(operatorStack.pop());

      if (operatorStack[operatorStack.length - 1] === "(") operatorStack.pop(); // Pop the '('
    } else if (isOperator(token)) {
      while (operatorStack.length && precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]) {
        output.push(operatorStack.pop());
      }
      operatorStack.push(token);
    }
  }

  while (operatorStack.length) {
    output.push(operatorStack.pop());
  }

  return output;
}

export function evaluatePostfix(tokens, cellId) {
  const evaluationStack = [];

  for (const token of tokens) {
    if (token === null) {
      evaluationStack.push({ value: null });
    }
    if (!isNaN(parseFloat(token))) {
      evaluationStack.push({ value: parseFloat(token) });
    } else if (isCell(token)) {
      evaluationStack.push({ value: token, type: "cell" });
    } else if (isRange(token)) {
      evaluationStack.push({ value: token, type: "range" });
    } else if (["true", "false"].includes(token?.toLowerCase())) {
      evaluationStack.push(parseStringToType(token));
    } else if (isStringLiteral(token)) {
      evaluationStack.push({ value: token.replaceAll('"', "") });
    } else if (isOperator(token) && !singleOperands.includes(token)) {
      const obj2 = evaluationStack.pop();
      const obj1 = evaluationStack.pop();
      const operand2 = obj2.type === "cell" ? functions.getCellValue(obj2.value).value : obj2.value;
      const operand1 = obj1.type === "cell" ? functions.getCellValue(obj1.value).value : obj1.value;
      switch (token) {
        case ",":
          if (Array.isArray(operand1)) {
            evaluationStack.push({ value: [...operand1, obj2], type: "array" });
          } else {
            evaluationStack.push({ value: [obj1, obj2], type: "array" });
          }
          break;
        case ":":
          evaluationStack.push({ value: `${obj1.value}:${obj2.value}`, type: "range" });
          break;
        case "+":
          evaluationStack.push({ value: operand1 + operand2 });
          break;
        case "-":
          evaluationStack.push({ value: operand1 - operand2 });
          break;
        case "*":
          evaluationStack.push({ value: operand1 * operand2 });
          break;
        case "/":
          evaluationStack.push({ value: operand1 / operand2 });
          break;
        case "^":
          evaluationStack.push({ value: Math.pow(operand1, operand2) });
          break;
        case ">":
          evaluationStack.push({
            value:
              functions.utilities.getBooleanValue(operand1) > functions.utilities.getBooleanValue(operand2) ? 1 : 0,
          });
          break;
        case "<":
          evaluationStack.push({
            value:
              functions.utilities.getBooleanValue(operand1) < functions.utilities.getBooleanValue(operand2) ? 1 : 0,
          });
          break;
        case "<=":
          evaluationStack.push({
            value:
              functions.utilities.getBooleanValue(operand1) <= functions.utilities.getBooleanValue(operand2) ? 1 : 0,
          });
          break;
        case ">=":
          evaluationStack.push({
            value:
              functions.utilities.getBooleanValue(operand1) >= functions.utilities.getBooleanValue(operand2) ? 1 : 0,
          });
          break;
        case "=":
          evaluationStack.push({ value: operand1 == operand2 ? 1 : 0 });
          break;
        case "&":
          evaluationStack.push({ value: String(operand1) + String(operand2) });
          break;
      }
    } else if (isOperator(token) && singleOperands.includes(token)) {
      const obj = evaluationStack.pop();
      const operand = obj.type === "cell" ? functions.getCellValue(obj.value).value : obj.value;
      switch (token) {
        case "%":
          evaluationStack.push({ value: operand / 100 });
          break;
      }
    } else if (isFunction(token)) {
      let obj = evaluationStack.pop();
      evaluationStack.push(functions[token?.toLowerCase()](Array.isArray(obj?.value) ? obj?.value : [obj], cellId));
    }
  }
  if (evaluationStack[0].type === "cell") {
    return functions.getCellValue(evaluationStack[0].value);
  }
  return evaluationStack[0];
}

export function evaluateFunction(infixExpression, cellId) {
  const postfixExpression = infixToPostfix(infixExpression);
  return evaluatePostfix(postfixExpression, cellId);
}

export function setFormulaUtilityFunctions(
  getCellValue,
  getRangeValue,
  getCellTitle,
  getColumnNumber,
  getRowNumber,
  getRangeTitle
) {
  if (functions.getCellValue != getCellValue) {
    functions.getCellValue = getCellValue;
    functions.getRangeValue = getRangeValue;
    functions.getCellTitle = getCellTitle;
    functions.getRangeTitle = getRangeTitle;
    functions.getColumnNumber = getColumnNumber;
    functions.getRowNumber = getRowNumber;
  }
}
